import React from 'react';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { DatabaseContext } from '../database/DatabaseHandler';
import PrestaGlobals from '../common/PrestaGlobals';
import AdminApi, { IPropertyRef, ITargetTypeRef } from './AdminApi';

const DEFAULT_DESCRIPTOR = '{ "label": "", "hidden": true }';

/**
 * EA-445: create a new (typically hidden) parameter for the current product — the margins workflow.
 * `property` is picked from the controlled vocabulary (incl. "Margin"); the parameter's type follows
 * the property's type server-side. On success the scripts reload, so the new param shows up in the
 * list / style script / 3D. Definition is global; the optional initial value is saved per shop.
 */
const AddParamForm: React.FC = () => {
    const data = React.useContext(DatabaseContext);
    const [properties, setProperties] = React.useState<IPropertyRef[]>([]);
    const [targetTypes, setTargetTypes] = React.useState<ITargetTypeRef[]>([]);

    const [name, setName] = React.useState('');
    const [property, setProperty] = React.useState('');
    const [targetPath, setTargetPath] = React.useState('');
    const [targetType, setTargetType] = React.useState('');
    const [descriptor, setDescriptor] = React.useState(DEFAULT_DESCRIPTOR);
    const [value, setValue] = React.useState('');
    const [status, setStatus] = React.useState<string | null>(null);
    const [busy, setBusy] = React.useState(false);

    React.useEffect(() => {
        AdminApi.listProperties().then(p => setProperties(p ?? []));
        AdminApi.listTargetTypes().then(t => setTargetTypes(t ?? []));
    }, []);

    let descriptorError: string | null = null;
    try { JSON.parse(descriptor); } catch (e: any) { descriptorError = e?.message ?? 'Érvénytelen JSON'; }

    const canSubmit = name.trim() !== '' && property !== '' && !descriptorError && !busy;

    const handleCreate = async () => {
        setBusy(true);
        setStatus(null);
        const res = await AdminApi.createParam(PrestaGlobals.productId, {
            name: name.trim(), property, target_path: targetPath, target_type: targetType,
            descriptor, value,
        });
        setBusy(false);
        if (res && 'success' in res && res.success) {
            setStatus(`Létrehozva: #${res.id} „${name.trim()}”${res.valueSaved ? ' (értékkel)' : ''}`);
            setName(''); setTargetPath(''); setValue(''); setDescriptor(DEFAULT_DESCRIPTOR);
            data.reloadScripts();
        } else {
            setStatus('Hiba: ' + (res && 'error' in res ? res.error : 'ismeretlen'));
        }
    };

    return (
        <Accordion disableGutters sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Új paraméter hozzáadása</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={1.5}>
                    <TextField
                        label="Név (globálisan egyedi)" size="small" value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="pl. BackMargin_p25"
                    />
                    <TextField
                        label="Tulajdonság (property)" size="small" select value={property}
                        onChange={(e) => setProperty(e.target.value)}
                    >
                        {properties.map(p => (
                            <MenuItem key={p.id} value={p.property}>{p.property} — {p.type}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Cél útvonal (target path)" size="small" value={targetPath}
                        onChange={(e) => setTargetPath(e.target.value)}
                        placeholder="pl. *.CabinetFrame.Back"
                        helperText="A „Stílus szkript” fülön láthatók az érvényes útvonalak."
                    />
                    <TextField
                        label="Cél típus (target type) — opcionális" size="small" select value={targetType}
                        onChange={(e) => setTargetType(e.target.value)}
                    >
                        <MenuItem value="">(nincs)</MenuItem>
                        {targetTypes.map(t => (
                            <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Leíró (descriptor JSON)" size="small" multiline minRows={2}
                        value={descriptor} onChange={(e) => setDescriptor(e.target.value)}
                        error={!!descriptorError}
                        helperText={descriptorError ? `JSON hiba: ${descriptorError}` : 'hidden: true → nem jelenik meg a vásárlónak'}
                        sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
                    />
                    <TextField
                        label="Kezdő érték (opcionális, a bolthoz)" size="small" value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="pl. 0mm,0mm,30mm,0mm,0mm,0mm"
                    />
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button variant="contained" size="small" disabled={!canSubmit} onClick={handleCreate}>
                            {busy ? 'Létrehozás…' : 'Létrehozás'}
                        </Button>
                        {status && <Typography variant="caption" color="text.secondary">{status}</Typography>}
                    </Stack>
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};

export default AddParamForm;
